/* eslint-disable react/react-in-jsx-scope */
import { Box, Slider, TextField } from "@mui/material";
import { useState, useEffect } from "react";

export function DualRangeInput({
  label,
  minLimit = 0,
  maxLimit = 100,
  value,
  onChange,
  unit = "",
  step = 10,
}: {
  label: string;
  minLimit?: number;
  maxLimit?: number;
  value?: [number, number] | undefined;
  onChange: (v: [number, number] | undefined) => void;
  unit?: string;
  step?: number;
}) {
  const [local, setLocal] = useState<[string, string]>(["", ""]);

  // Sync local text fields when parent value changes
  useEffect(() => {
    if (!value) {
      // no range selected → empty inputs
      setLocal(["", ""]);
    } else {
      setLocal([String(value[0]), String(value[1])]);
    }
  }, [value]);

  // Clean & enforce limits
  const clamp = (min: number, max: number): [number, number] => {
    if (min < minLimit) min = minLimit;
    if (max > maxLimit) max = maxLimit;
    if (min > max) min = max;
    return [min, max];
  };

  // Apply changes to parent
  const applyChange = (min: string, max: string) => {
    if (min === "" && max === "") {
      onChange(undefined); // means filter removed
      return;
    }

    const parsedMin = min === "" ? minLimit : Number(min);
    const parsedMax = max === "" ? maxLimit : Number(max);

    const [cMin, cMax] = clamp(parsedMin, parsedMax);
    onChange([cMin, cMax]);
  };

  // Slider value fallback
  const sliderValue: [number, number] = value ?? [minLimit, maxLimit];

  const blockInvalidChars = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const sanitizeNumericInput = (value: string) => value.replace(/\D/g, "");

  return (
    <Box sx={{ mt: 2 }}>
      <Box display="flex" gap={1} mb={2}>
        {/* MIN */}
        <TextField
          className="filter-input"
          //   label={`${label} Min`}
          type="number"
          value={local[0]}
          placeholder="Min"
          onKeyDown={blockInvalidChars}
          onChange={(e) => {
            const val = sanitizeNumericInput(e.target.value);
            setLocal([val, local[1]]);
          }}
          onBlur={() => applyChange(local[0], local[1])}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            min: minLimit,
          }}
          InputProps={{ endAdornment: unit }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": {
              width: "100% !important",
            },
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
              {
                display: "none",
              },
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
          }}
        />

        {/* MAX */}
        <TextField
          className="filter-input"
          //   label={`${label} Max`}
          type="number"
          value={local[1]}
          placeholder="Max"
          onKeyDown={blockInvalidChars}
          onChange={(e) => {
            const val = sanitizeNumericInput(e.target.value);
            setLocal([local[0], val]);
          }}
          onBlur={() => applyChange(local[0], local[1])}
          InputProps={{ endAdornment: unit }}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            min: minLimit,
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": {
              width: "100% !important",
            },
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
              {
                display: "none",
              },
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
          }}
        />
      </Box>

      {/* SLIDER */}
      <Slider
        value={sliderValue}
        disableSwap
        step={step}
        onChange={(_, newValue) => {
          const [minVal, maxVal] = newValue as number[];
          applyChange(String(minVal), String(maxVal));
        }}
        valueLabelDisplay="auto"
        className="planners-filters__slider"
        min={minLimit}
        max={maxLimit}
        sx={{
          "& .MuiSlider-thumb": {
            backgroundColor: "#fff",
          },
          "& .MuiSlider-rail": {
            backgroundColor: "#e2e8f0",
          },
        }}
      />
    </Box>
  );
}
